// Utilitas absensi untuk tampilan: jam sesi, keterangan kedatangan, dan simbol sel Rekap.
// Aturan keterangan yang SEBENARNYA (simpan & turunan untuk data lama) ada di backend:
// src/utils/ketepatan.js. Yang di sini harus selaras dengannya.

// "18.45" / "18:45" / "9:05" → menit sejak 00:00, atau null jika tak valid.
export function hhmmToMin(v) {
  const m = /^([01]?\d|2[0-3])[.:]([0-5]\d)$/.exec(String(v || "").trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function minToHHMM(min) {
  const c = Math.max(0, Math.min(23 * 60 + 59, min));
  return (
    String(Math.floor(c / 60)).padStart(2, "0") + ":" + String(c % 60).padStart(2, "0")
  );
}

// ── Keterangan kedatangan (On Time · Late · Very Late) ────────────────────────
// Dipilih trainer saat menandai hadir. Batas di bawah hanya PANDUAN yang ditampilkan ke
// trainer: jam tombol ditekan bukan jam murid datang, jadi tak ada yang dihitung di sini.
export const ONTIME_MAX_MIN = 10; // ≤ 10 menit setelah kelas mulai → On Time
export const LATE_MAX_MIN = 20;   // 11–20 menit → Late; lebih dari itu → Very Late

export const PUNCTUALITY = ["ontime", "late", "verylate"];

// `pick`  = tombol "tandai hadir" di Belum Hadir — ketiganya sama bobotnya, tak ada yang
//           tampil sebagai pilihan bawaan.
// `on`    = pilihan yang sedang berlaku di pemilih keterangan.
// `short` + `soft` = sel Rekap (simbolnya sama dengan ekspor Excel, lihat recapSymbol).
export const PUNCTUALITY_STYLES = {
  ontime: {
    label: "On Time",
    short: "✓",
    dot: "bg-green-500",
    soft: "bg-green-500/15 text-green-600",
    pick: "border-green-300 bg-green-50 text-green-700 hover:bg-green-500 hover:text-white dark:border-green-700/50 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-600",
    on: "bg-green-500 text-white"
  },
  late: {
    label: "Late",
    short: "L",
    dot: "bg-amber-500",
    soft: "bg-amber-500/15 text-amber-600",
    pick: "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-500 hover:text-white dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-600",
    on: "bg-amber-500 text-white"
  },
  verylate: {
    label: "Very Late",
    short: "VL",
    dot: "bg-red-500",
    soft: "bg-red-500/15 text-red-600",
    pick: "border-red-300 bg-red-50 text-red-700 hover:bg-red-500 hover:text-white dark:border-red-700/50 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-600",
    on: "bg-red-500 text-white"
  }
};

// Hadir tanpa keterangan: absensi lama yang tak punya jam (atau sesinya tak terbaca), jadi
// server pun tak bisa menurunkannya. Tetap HADIR — tetap dihitung.
export const PRESENT_UNRATED = {
  label: "Hadir tanpa keterangan",
  short: "H",
  soft: "bg-slate-500/15 text-slate-600 dark:text-slate-300"
};

// Panduan jam satu sesi, dari jam mulainya: "16.00 - 17.30" →
// { start: "16.00", ontimeUntil: "16.10", lateFrom: "16.11", lateUntil: "16.20" }.
// Sesi tak terbaca → null (halaman lalu menyebut batas menitnya saja).
export function punctualityGuide(session) {
  const start = hhmmToMin(String(session || "").split("-")[0]);
  if (start === null) return null;
  const jam = (m) => minToHHMM(m).replace(":", ".");
  return {
    start: jam(start),
    ontimeUntil: jam(start + ONTIME_MAX_MIN),
    lateFrom: jam(start + ONTIME_MAX_MIN + 1),
    lateUntil: jam(start + LATE_MAX_MIN)
  };
}

// ── Simbol sel Rekap ──────────────────────────────────────────────────────────
// DIPAKAI BERSAMA layar Rekap dan ekspor Excel-nya, supaya simbol yang sama selalu berarti
// hal yang sama di keduanya — dulu "A" berarti Hadir di layar tapi Absen di Excel.
//   ✓ On Time · L Late · VL Very Late · H hadir tanpa keterangan  → hadir (dihitung)
//   I Izin · A tidak masuk                                         → tidak hadir
// "Izin" DAN "Permission" sama-sama diterima: nilai warisan dengan dua ejaan; penulisan baru
// hanya "Attend" (STATUS_SAH di attendance.controller). Status lain yang tak dikenal
// ditampilkan apa adanya (`kind: "other"`).
// Sel kosong → null.
export function recapSymbol(raw, punctuality) {
  const status = String(raw || "").split(":")[0];
  if (!status) return null;
  if (status === "Attend") {
    const kind = PUNCTUALITY.includes(punctuality) ? punctuality : "unrated";
    const s = PUNCTUALITY_STYLES[kind] || PRESENT_UNRATED;
    return { kind, symbol: s.short, label: s.label, hadir: true };
  }
  if (status === "Izin" || status === "Permission") {
    return { kind: "izin", symbol: "I", label: "Izin", hadir: false };
  }
  if (status === "Absent") return { kind: "absent", symbol: "A", label: "Tidak masuk", hadir: false };
  return { kind: "other", symbol: status, label: status, hadir: false };
}
